import api from "./client";
import type { CanalVenda } from "./configuracao";


// ==================== TROCAS DE PRODUTOS ====================

export interface RetornoTiny {
	id: number;
	descricao: string;
	sku: string;
	preco: string;
	ean?: string;
	gtin?: string;
	quantidade?: number;
}


export interface Troca {
	troca_id: number;
	data_enviado: string;
	data_trocado: string | null;
	status: "pendente" | "concluida" | "cancelada";
	/** Indica se alguém iniciou a troca (para saber qual troca o outro está fazendo). */
	iniciada?: boolean;
	/** Data/hora em que a troca foi iniciada. */
	data_inicio_troca?: string | null;
	/** Usuário que iniciou a troca. */
	usuario_iniciou?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	erro_inicial: string;
	tipo_erro:
		| "estoque negativo"
		| "nao encontrado"
		| "sem relacionar"
		| "defeito"
		| "outro"
		// Falta que a bancada de embalagem encontrou num pedido já separado (#1931).
		| "divergencia embalagem"
		| null;
	retorno_tiny: RetornoTiny | null;
	observacao: string | null;
	usuario?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	};
	/**
	 * Usuário que efetivamente concluiu a troca, independente do caminho
	 * (manual via UI/script, auto pelo sync com débito, auto sem ocorrência).
	 * Fonte de verdade para a coluna "Usuário" da listagem de concluídas.
	 */
	usuario_concluiu?: {
		usuario_id: number;
		usuario: string;
		responsavel: string;
	} | null;
	produto_troca?: {
		produto_estoque_id: number;
		produto: {
			produto_id: number;
			descricao: string;
			sku: string;
			ean: string;
			url_foto?: string;
		};
	};
	produto_original?: {
		produto_estoque_id: number;
		produto: {
			produto_id: number;
			descricao: string;
			sku: string;
			ean: string;
			url_foto?: string;
		};
	};
	pedido?: {
		pedido_id: number;
		tiny_id?: number;
		num_pedido: string;
		data: string;
		canal_venda?: string | CanalVenda | null;
	};
}


export interface CreateTrocaDto {
	erro_inicial: string;
	tipo_erro?:
		| "estoque negativo"
		| "nao encontrado"
		| "sem relacionar"
		| "defeito"
		| "outro";
	retorno_tiny: string;
	observacao?: string;
	usuario_id?: number;
	produto_estoque_id?: number;
	pedido_id: number;
}


// `produto_estoque_id` fica de fora: o produto de substituição é definido pela
// conclusão automática, não por PATCH (#1422). O backend também o omite do
// UpdateTrocaDto.
export interface UpdateTrocaDto
	extends Partial<Omit<CreateTrocaDto, "produto_estoque_id">> {
	status?: "pendente" | "concluida" | "cancelada";
	data_trocado?: string;
}


/**
 * Buscar todas as trocas
 */
export const buscarTrocas = async (params?: {
	search?: string;
	offset?: number;
	limit?: number;
	sort_field?: string;
	sort_direction?: "asc" | "desc";
	status?: string;
	data_inicio?: string;
	data_fim?: string;
	canal_venda?: string;
	status_estoque?: string;
	/** "true" = iniciadas, "false" = não iniciadas, undefined = todas */
	iniciada?: string;
	signal?: AbortSignal;
}): Promise<{ results: Troca[]; total: number }> => {
	try {
		const queryParams: Record<string, any> = {};

		if (params?.search) queryParams.search = params.search;
		if (params?.offset !== undefined)
			queryParams.offset = params.offset.toString();
		if (params?.limit !== undefined)
			queryParams.limit = params.limit.toString();
		if (params?.sort_field) queryParams.sort_field = params.sort_field;
		if (params?.sort_direction)
			queryParams.sort_direction = params.sort_direction;
		if (params?.status) queryParams.status = params.status;
		if (params?.data_inicio) queryParams.data_inicio = params.data_inicio;
		if (params?.data_fim) queryParams.data_fim = params.data_fim;
		if (params?.canal_venda) queryParams.canal_venda = params.canal_venda;
		if (params?.status_estoque) queryParams.status_estoque = params.status_estoque;
		if (params?.iniciada) queryParams.iniciada = params.iniciada;

		const response = await api.get("/troca", { params: queryParams, signal: params?.signal });
		return response.data;
	} catch (error: any) {
		if (error.name === "CanceledError" || error.code === "ERR_CANCELED") throw error;
		console.error("Erro ao buscar trocas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar trocas.");
	}
};


/**
 * Buscar estatísticas de trocas
 */
export const buscarEstatisticasTrocas = async (
	signal?: AbortSignal,
): Promise<{
	total: number;
	pendentes: number;
	concluidas: number;
	canceladas: number;
}> => {
	try {
		const response = await api.get("/troca/estatisticas", { signal });
		return response.data;
	} catch (error: any) {
		if (error.name === "CanceledError" || error.code === "ERR_CANCELED") throw error;
		console.error("Erro ao buscar estatísticas de trocas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar estatísticas de trocas.");
	}
};


/**
 * Buscar estatísticas de trocas com filtros aplicados
 */
export const buscarEstatisticasTrocasFiltradas = async (params?: {
	search?: string;
	canal_venda?: string;
	data_inicio?: string;
	data_fim?: string;
	status_estoque?: string;
	signal?: AbortSignal;
}): Promise<{
	total: number;
	pendentes: number;
	concluidas: number;
	canceladas: number;
}> => {
	try {
		const queryParams: Record<string, any> = {};

		if (params?.search) queryParams.search = params.search;
		if (params?.canal_venda) queryParams.canal_venda = params.canal_venda;
		if (params?.data_inicio) queryParams.data_inicio = params.data_inicio;
		if (params?.data_fim) queryParams.data_fim = params.data_fim;
		if (params?.status_estoque) queryParams.status_estoque = params.status_estoque;

		const response = await api.get("/troca/estatisticas-filtradas", {
			params: queryParams,
			signal: params?.signal,
		});
		return response.data;
	} catch (error: any) {
		if (error.name === "CanceledError" || error.code === "ERR_CANCELED") throw error;
		console.error("Erro ao buscar estatísticas filtradas de trocas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar estatísticas filtradas de trocas.");
	}
};


/**
 * Buscar contagem de trocas pendentes por status de estoque
 */
export const buscarContagemEstoqueTrocas = async (params?: {
	search?: string;
	canal_venda?: string;
	data_inicio?: string;
	data_fim?: string;
	signal?: AbortSignal;
}): Promise<{
	total: number;
	possui: number;
	insuficiente: number;
	sem: number;
	nao_encontrado: number;
}> => {
	try {
		const queryParams: Record<string, any> = {};

		if (params?.search) queryParams.search = params.search;
		if (params?.canal_venda) queryParams.canal_venda = params.canal_venda;
		if (params?.data_inicio) queryParams.data_inicio = params.data_inicio;
		if (params?.data_fim) queryParams.data_fim = params.data_fim;

		const response = await api.get("/troca/contagem-estoque", {
			params: queryParams,
			signal: params?.signal,
		});
		return response.data;
	} catch (error: any) {
		if (error.name === "CanceledError" || error.code === "ERR_CANCELED") throw error;
		console.error("Erro ao buscar contagem de estoque de trocas:", error);
		return { total: 0, possui: 0, insuficiente: 0, sem: 0, nao_encontrado: 0 };
	}
};


export interface EstatisticasTrocasDetalhadas {
	total: number;
	total_pedidos: number;
	por_canal_venda: Array<{ canal_venda: string; quantidade: number }>;
	por_canal_venda_pendentes?: Array<{
		canal_venda: string;
		quantidade: number;
	}>;
	por_erro_inicial: Array<{ erro_inicial: string; quantidade: number }>;
	por_tipo_erro: Array<{ tipo_erro: string; quantidade: number }>;
	por_status: Array<{ status: string; quantidade: number }>;
	produtos_trocados: Array<{
		descricao: string;
		sku: string | null;
		quantidade: number;
	}>;
	armazens_nao_encontrado: {
		total: number;
		itens: Array<{
			armazem_id: number | null;
			nome: string;
			quantidade: number;
		}>;
		area_reposicao: Array<{
			armazem_id: number | null;
			nome: string;
			quantidade: number;
		}>;
	};
}


export const buscarEstatisticasTrocasDetalhadas = async (params?: {
	search?: string;
	status?: string;
	canal_venda?: string;
	data_inicio?: string;
	data_fim?: string;
}): Promise<EstatisticasTrocasDetalhadas> => {
	try {
		const queryParams: Record<string, any> = {};

		if (params?.search) queryParams.search = params.search;
		if (params?.status) queryParams.status = params.status;
		if (params?.canal_venda) queryParams.canal_venda = params.canal_venda;
		if (params?.data_inicio) queryParams.data_inicio = params.data_inicio;
		if (params?.data_fim) queryParams.data_fim = params.data_fim;

		const response = await api.get("/troca/estatisticas-detalhadas", {
			params: queryParams,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar estatísticas detalhadas de trocas:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar estatísticas detalhadas de trocas.");
	}
};


/**
 * Buscar uma troca por ID
 */
export const buscarTrocaPorId = async (id: number): Promise<Troca> => {
	try {
		const response = await api.get(`/troca/${id}`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao buscar troca:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao buscar troca.");
	}
};


/**
 * Criar uma nova troca
 */
export const criarTroca = async (dados: CreateTrocaDto): Promise<Troca> => {
	try {
		const response = await api.post("/troca", dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao criar troca:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao criar troca.");
	}
};


/**
 * Atualizar uma troca
 */
export const atualizarTroca = async (
	id: number,
	dados: UpdateTrocaDto,
): Promise<Troca> => {
	try {
		const response = await api.patch(`/troca/${id}`, dados);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao atualizar troca:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao atualizar troca.");
	}
};


/**
 * Marca a troca como iniciada (para indicar que alguém está trabalhando nela).
 */
export const iniciarTroca = async (troca_id: number): Promise<Troca> => {
	try {
		const response = await api.post(`/troca/${troca_id}/iniciar`);
		return response.data;
	} catch (error: any) {
		console.error("Erro ao iniciar troca:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao iniciar troca.");
	}
};


/** Quanto da falta saiu das mãos do líder numa reavaliação. */
export interface FaltaReavaliada {
	num_pedido: string;
	unidades_em_separacao: number;
	unidades_em_reposicao: number;
}

/**
 * Recalcula a falta da ocorrência de divergência na embalagem (#1931): com
 * saldo no armazém principal, ela sai do líder e vai para separação; com saldo
 * em outro armazém, para reposição.
 */
export const reavaliarFaltaDaOcorrencia = async (
	troca_id: number,
): Promise<FaltaReavaliada> => {
	const response = await api.post(`/troca/${troca_id}/reavaliar-falta`);
	return response.data;
};

/**
 * Cancelar uma troca manualmente
 * Adiciona marcador "troca cancelada" ao pedido e mantém o pedido aprovado
 */
export const cancelarTrocaManual = async (
	troca_id: number,
	pedido_id: number,
	tiny_id: number,
): Promise<Troca> => {
	try {
		const response = await api.post(`/troca/${troca_id}/cancelar-manual`, {
			pedido_id,
			tiny_id,
		});
		return response.data;
	} catch (error: any) {
		console.error("Erro ao cancelar troca manualmente:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao cancelar troca manualmente.");
	}
};


/**
 * Deletar uma troca
 */
export const deletarTroca = async (id: number): Promise<void> => {
	try {
		await api.delete(`/troca/${id}`);
	} catch (error: any) {
		console.error("Erro ao deletar troca:", error);
		if (error.response?.data?.message) {
			throw new Error(error.response.data.message);
		}
		throw new Error("Falha ao deletar troca.");
	}
};

